import React, { useCallback, useState } from 'react';
import { memo } from 'react';
import BaseNode from '../BaseNode';
import { Box, Typography, Chip, alpha, useTheme, Tabs, Tab } from '@mui/material';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import { DynamicControls } from '../../core/NodeControls';
import { getLayerParameters } from '../../utils/tensorflowParameters';

/**
 * DropoutNode represents a dropout layer in the neural network
 */
const DropoutNode = ({ data, id, selected, isConnectable }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  
  // Get the TensorFlow parameters for the Dropout layer
  const dropoutParams = getLayerParameters('Dropout');
  
  // Handler to update node data
  const handleDataChange = useCallback((key, value) => {
    if (data.onChange) {
      data.onChange(id, key, value);
    }
  }, [data, id]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Generate preview content
  const renderPreview = () => {
    return (
      <>
        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1, textAlign: 'center' }}>
          Rate: {data.rate || 0.2}
        </Typography>
        
        {/* Visual representation of dropout */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {Array.from({ length: 16 }).map((_, i) => {
            const isDropped = i < Math.floor(16 * (data.rate || 0.2));
            return (
              <Box
                key={i}
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '1px',
                  backgroundColor: isDropped ? alpha(theme.palette.error.main, 0.3) : theme.palette.primary.main,
                  margin: '2px',
                  opacity: isDropped ? 0.4 : 1,
                }}
              />
            );
          })}
        </Box>
        
        <Chip 
          label={`${Math.round((data.rate || 0.2) * 100)}% dropped`} 
          size="small"
          sx={{ 
            fontSize: '0.7rem',
            mt: 1,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
          }}
        />
      </>
    );
  };
  
  // Define which parameters should appear in the basic tab
  const basicParams = ['rate'];
  
  // Controls for the node configuration popover with tabs for basic and advanced options
  const renderControls = () => {
    return (
      <>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          variant="fullWidth"
        >
          <Tab label="Basic" />
          <Tab label="Advanced" />
        </Tabs>
        
        {/* Basic parameters tab */}
        {tabValue === 0 && (
          <DynamicControls
            parameters={dropoutParams}
            values={data}
            onChange={handleDataChange}
            filter={(key) => basicParams.includes(key)}
          />
        )}
        
        {/* Advanced parameters tab */}
        {tabValue === 1 && (
          <DynamicControls
            parameters={dropoutParams}
            values={data}
            onChange={handleDataChange}
            filter={(key) => !basicParams.includes(key)}
          />
        )}
      </>
    );
  };
  
  return (
    <BaseNode
      data={data}
      id={id}
      selected={selected}
      isConnectable={isConnectable}
      icon={<FilterNoneIcon fontSize="small" />}
      nodeType="utility"
      preview={renderPreview()}
      controls={renderControls()}
      onDelete={data.onDelete}
    />
  );
};

export default memo(DropoutNode); 